import merge from 'lodash/merge'
import get from 'lodash/get'
import crossFetch from 'cross-fetch'
import { requiredCheck } from '../required-check'

export async function fetch(
  url: string,
  options: RequestInit,
  {
    lean = false,
    includeDefaultOptions = true,
    timeout,
  }: {
    lean?: boolean
    includeDefaultOptions?: boolean
    timeout?: number
  } = {}
) {
  requiredCheck(url, 'url')

  const finalUrl = url

  //set the default options
  const finalOptions: RequestInit = includeDefaultOptions
    ? merge(
        {},
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
        options
      )
    : options

  const hasTimeout = typeof timeout === 'number' && Number.isFinite(timeout) && timeout > 0

  let response: Response

  if (hasTimeout) {
    const controller = new AbortController()
    const providedSignal = (finalOptions as RequestInit & { signal?: AbortSignal }).signal
    const onProvidedAbort = () => controller.abort(providedSignal?.reason)

    if (providedSignal) {
      if (providedSignal.aborted) {
        controller.abort(providedSignal.reason)
      } else {
        providedSignal.addEventListener('abort', onProvidedAbort, { once: true })
      }
    }

    const fetchPromise = crossFetch(finalUrl, { ...finalOptions, signal: controller.signal })

    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort()
        reject(new Error(`Request timed out after ${timeout}ms`))
      }, timeout)
    })

    try {
      response = (await Promise.race([fetchPromise, timeoutPromise])) as Response
    } finally {
      if (providedSignal && providedSignal.removeEventListener) {
        providedSignal.removeEventListener('abort', onProvidedAbort)
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  } else {
    response = await crossFetch(finalUrl, finalOptions)
  }
  //should handle the error here too
  //for now just throw error
  const status = get(response, 'status')

  if (lean) {
    return response
  } else {
    if (status >= 400) {
      //just throw error if there is no specific handling
      // const statusText = get(response, 'statusText')
      // let textResult = await response.text()
      // if (!textResult) textResult = 'NO_TEXT_RESPONSE'
      // const message = `${statusText} - ${textResult}`
      const message = await parseErrorResponse(response)
      throw new Error(message)
    } else {
      //there is no specific way for handling the response
      // so by default handle the json object
      return response.json()
    }
  }
}

export async function parseErrorResponse(res: Response): Promise<string> {
  const message = 'Server returned error!'
  try {
    const ct = res.headers.get('content-type') || ''
    const text = await res.text()
    if (ct.includes('application/json')) {
      try {
        const data = JSON.parse(text)
        const m = (data && (data.error || data.errors || data.title || data.detail || data.message)) as unknown

        //if json return the trimmed error
        if (typeof m === 'string' && m.trim()) return m.trim()
      } catch {
        //just ignore the error here and move to patch it using the text
      }
    }

    //now if is it a string, try to get the error message
    const preMatch = text.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
    let content = preMatch ? preMatch[1] : text
    content = content
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
    const lines = content
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    const errLine = lines.find((s) => s.startsWith('Error:')) || lines[0]
    if (errLine) {
      if (errLine.startsWith('Error:')) {
        const withoutPrefix = errLine.replace(/^Error:\s*/, '')

        return withoutPrefix.trim()
      }
      return errLine
    }
  } catch {
    //this will fallback to the default error message
  }
  return message
}
